import { FormControl } from '@angular/forms'
import {
  NOTE_MAX_LENGTH,
  NoteContentValidator,
  checkIsNoteValid,
} from '@helios/validators'
import {
  Break,
  BreakCodeResponse,
  resolveBreakCodes,
} from '../model/break.model'
import {
  BulkBreakGroup,
  allBreaksNoted,
  buildBreakGroups,
} from '../model/bulk-break-group.model'
import {
  actionCodeOptions,
  descriptionCodeOptions,
  notesRequiredActionCodes,
} from './break-code-validation'
import { KeepMode, MIXED_CODE_SETS_REASON } from './bulk-break-validation'

export interface EditNoteSelection {
  actionCode: string | null
  descriptionCode: string | null
  note: string
}

export interface EditNoteValidation {
  submitDisabled: boolean
  /** First/most-important blocker; '' when none. */
  reason: string
  /** Codes common to ALL selected breaks — the dropdown items. */
  actionOptions: string[]
  descriptionOptions: string[]
  /** Action codes (by string) whose notesRequired is true — for the ◆ marker. */
  notesRequiredActionCodeSet: Set<string>
  /** Both common sets empty → render the single empty-state box. */
  bothCommonEmpty: boolean
  /** A code is required for a write target but no common code is shared → "narrow". */
  codeRequiredButUnavailable: boolean
  /** A note must be entered (no codes available, or the chosen code is notes-required).
   *  Drives the note control's allowEmpty so an empty required note red-borders. */
  noteRequired: boolean
}

const NARROW = (field: 'action' | 'description'): string =>
  `Selected breaks don't share a common ${field} code — narrow your selection.`

export interface CommonSelectionCodes {
  actionOptions: string[]
  descriptionOptions: string[]
  notesRequiredActionCodeSet: Set<string>
}

/**
 * Codes common to ALL selected breaks — every break type AND every account —
 * computed via the per-type group intersection (each group already intersects
 * across its accounts). Mirrors BulkBreakModalComponent.commonCodes, so the
 * Edit-Note dropdown matches the sibling dialog and the requirement computation
 * (which also uses buildBreakGroups), rather than reading a single account's codes.
 * Returns empty sets unless the selection resolves to a single ready code set.
 */
export function commonSelectionCodes(
  breaks: Break[],
  breakCodes: BreakCodeResponse | undefined,
): CommonSelectionCodes {
  const empty: CommonSelectionCodes = {
    actionOptions: [],
    descriptionOptions: [],
    notesRequiredActionCodeSet: new Set<string>(),
  }
  if (resolveBreakCodes(breaks, breakCodes).status !== 'ready') return empty
  const groups = buildBreakGroups(breaks, breakCodes)
  if (groups.length === 0) return empty

  const intersect = (pick: (g: BulkBreakGroup) => string[]): string[] => {
    const [first, ...rest] = groups
    return pick(first).filter(code => rest.every(g => pick(g).includes(code)))
  }
  const notesRequiredActionCodeSet = new Set<string>()
  for (const g of groups) {
    notesRequiredActionCodes(g.availability).forEach(c =>
      notesRequiredActionCodeSet.add(c),
    )
  }
  return {
    actionOptions: intersect(g => actionCodeOptions(g.availability)),
    descriptionOptions: intersect(g => descriptionCodeOptions(g.availability)),
    notesRequiredActionCodeSet,
  }
}

/**
 * Validation for the global break codes Edit-Note view, single or bulk.
 * Dropdown options are the all-selection intersection; whether a code is REQUIRED is
 * decided independently per field over that field's write targets (keep → breaks with
 * that field empty, override → all).
 */
export function editNoteValidation(
  breaks: Break[],
  breakCodes: BreakCodeResponse | undefined,
  selection: EditNoteSelection,
  keepMode: KeepMode,
): EditNoteValidation {
  const whole = resolveBreakCodes(breaks, breakCodes)
  const { actionOptions, descriptionOptions, notesRequiredActionCodeSet } =
    commonSelectionCodes(breaks, breakCodes)
  const bothCommonEmpty =
    actionOptions.length === 0 && descriptionOptions.length === 0
  const base = {
    actionOptions,
    descriptionOptions,
    notesRequiredActionCodeSet,
    bothCommonEmpty,
  }

  if (whole.status === 'unavailable') {
    return {
      ...base,
      submitDisabled: true,
      reason: 'Couldn’t load break codes — reopen the dialog to retry.',
      codeRequiredButUnavailable: false,
      noteRequired: false,
    }
  }
  if (whole.status === 'mixedCodeSets') {
    return {
      ...base,
      submitDisabled: true,
      reason: MIXED_CODE_SETS_REASON,
      codeRequiredButUnavailable: false,
      noteRequired: false,
    }
  }

  // Write targets are PER FIELD and the two are validated independently: override
  // writes both to every break; keep preserves a field that already holds a value and
  // fills an empty one (the backend resolves them separately). So a break coded on
  // action only still needs a description, and an action code chosen for it is a no-op.
  const targetsFor = (field: 'action' | 'description'): Break[] =>
    keepMode === 'override'
      ? breaks
      : breaks.filter(b =>
          field === 'action' ? !b.actionCode : !b.descriptionCode,
        )
  const actionTargets = targetsFor('action')
  const actionRequired = buildBreakGroups(actionTargets, breakCodes).some(
    g => actionCodeOptions(g.availability).length > 0,
  )
  const descriptionRequired = buildBreakGroups(
    targetsFor('description'),
    breakCodes,
  ).some(g => descriptionCodeOptions(g.availability).length > 0)

  const reasons: string[] = []
  if (actionRequired) {
    if (actionOptions.length === 0) reasons.push(NARROW('action'))
    else if (!selection.actionCode) reasons.push('Select an action code.')
  }
  if (descriptionRequired) {
    if (descriptionOptions.length === 0) reasons.push(NARROW('description'))
    else if (!selection.descriptionCode)
      reasons.push('Select a description code.')
  }

  const codeRequiredButUnavailable =
    (actionRequired && actionOptions.length === 0) ||
    (descriptionRequired && descriptionOptions.length === 0)

  // Note requirement. An empty note is a no-op (APPEND) that preserves every break's
  // existing note, so a preserved notes-required code is never stripped and does NOT
  // force a note. A valid note is required only when no codes are available anywhere
  // (the note is then the only thing to set), or the chosen action code is
  // notes-required AND is actually WRITTEN to an action write target. That guard is
  // essential: maybePrefill seeds selection.actionCode with the breaks' existing code,
  // so in keep mode with every action already coded that prefilled code must NOT force
  // a note — it's preserved, not re-applied. Either way the requirement is relaxed
  // when every break already carries a note (a blank note preserves each break's own).
  const anyCodesAvailable = buildBreakGroups(breaks, breakCodes).some(
    g =>
      actionCodeOptions(g.availability).length > 0 ||
      descriptionCodeOptions(g.availability).length > 0,
  )
  const selectedActionNotesRequired =
    actionTargets.length > 0 &&
    !!selection.actionCode &&
    notesRequiredActionCodeSet.has(selection.actionCode)
  const allBreaksAlreadyNoted = allBreaksNoted(breaks)
  const noteRequired =
    (!anyCodesAvailable || selectedActionNotesRequired) &&
    !allBreaksAlreadyNoted
  // A provided note OVERWRITES every selected break, so it must itself be a valid note
  const noteProvided = selection.note.length > 0
  if ((noteRequired || noteProvided) && !checkIsNoteValid(selection.note)) {
    reasons.push(
      selectedActionNotesRequired
        ? 'This action code requires a note (3 words minimum).'
        : 'A note (3 words minimum) is required.',
    )
  }
  const contentError = noteProvided
    ? NoteContentValidator()(new FormControl(selection.note))
    : null
  if (contentError) {
    reasons.push(
      'NoteTooLong' in contentError
        ? `Note must be at most ${NOTE_MAX_LENGTH} characters.`
        : `Note contains invalid characters (e.g. '<' or '>').`,
    )
  }

  return {
    ...base,
    submitDisabled: reasons.length > 0,
    reason: reasons[0] ?? '',
    codeRequiredButUnavailable,
    noteRequired,
  }
}
